import type { SessionKind } from '@pingo/shared';

/**
 * Session tokens per kind (a device may hold an admin USER session and a CADDY session at once).
 * localStorage so caddy sessions survive app restarts / offline.
 */
const KEYS: Record<SessionKind, string> = {
  USER: 'pingo.session.user',
  CADDY: 'pingo.session.caddy',
  PLAYER: 'pingo.session.player',
};

export interface StoredSession {
  token: string;
  expiresAt: string;
  kind: SessionKind;
}

type Listener = (kind: SessionKind, session: StoredSession | null) => void;
const listeners = new Set<Listener>();

export function getSession(kind: SessionKind): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEYS[kind]);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredSession;
    if (new Date(s.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(KEYS[kind]);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function setSession(session: StoredSession): void {
  localStorage.setItem(KEYS[session.kind], JSON.stringify(session));
  listeners.forEach((l) => l(session.kind, session));
}

export function clearSession(kind: SessionKind): void {
  localStorage.removeItem(KEYS[kind]);
  listeners.forEach((l) => l(kind, null));
}

export function onSessionChange(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
